import { v4 as uuidv4 } from 'uuid';
import { ChannelType } from '@shopee/shopee-chatbot-agent-chat/dist/types';

import agentStore from '@/stores/agent';
import { separateEnquiryType, generateOfflineFormUrl } from './tools';
import { WEB_FORM_AUTOFILL_FIELD, WEB_FORM_TYPE } from './locale';
import { userStore } from '@/stores';
import { LOYALTY_TIER } from '@/stores/user/constants';
import { autoFillFrom } from '@/services/webform/api';
import { FileType } from '@shopee/chatbot-ui/lib/components/BaseUpload';

const getFileDefaultName = (type: FileType, index = 1) => {
  const defaultNameMap: { [key: number]: string } = {
    [FileType.File]: `file-${index}`,
    [FileType.Image]: `image-${index}.png`,
    [FileType.Video]: `video-${index}.mp4`,
  };
  return defaultNameMap[type] || '';
};
export const getAutoFillInfoValueMap = () => {
  const fileNumMap: { [key: string]: number } = {};
  const fileList = (agentStore.files || []).map((item) => {
    const { type } = item;
    fileNumMap[type] ? fileNumMap[type]++ : fileNumMap[type] = 1;
    return {
      ...item,
      number: fileNumMap[type],
    };
  }).map((item) => {
    const { name, url, type, number } = item;
    const defaultName = getFileDefaultName(type, number);
    return {
      file_name: name || defaultName,
      url: url,
    };
  });
  const autoFillInfoValueMap = {
    [WEB_FORM_AUTOFILL_FIELD.userType]: agentStore.userTypeName,
    [WEB_FORM_AUTOFILL_FIELD.enquiryType]: agentStore.inhouseEnquiryTypeName,
    [WEB_FORM_AUTOFILL_FIELD.problemType]: agentStore.problemType,
    [WEB_FORM_AUTOFILL_FIELD.description]: agentStore.description,
    [WEB_FORM_AUTOFILL_FIELD.attachments]: fileList,
    [WEB_FORM_AUTOFILL_FIELD.orderSN]: agentStore.inboundOrderSn,
  };

  return autoFillInfoValueMap;
};
// 'pl', 'es', 'fr' 使用 emergencyTemplate，旧地区使用前端拼接的表单
// 目前过渡阶段 ID IH 使用 emergencyTemplate 下发，前端兜底，后续所有地区也采取一样的方式
export const getOfflineFormUrlWithParams = async () => {
  const baseParams = {} as any;

  if (agentStore.defaultEmergencyTemplate.webFormPlatform === ChannelType.inhouse) {
    const webFormUuid = agentStore.webFormUuid || uuidv4();
    agentStore.updateWebFormUuid(webFormUuid);
    baseParams.uuid = webFormUuid;

    const { INHOUSE_WEB_FORM_MAP } = await import('@/utils/webform');
    // IH 自动填充字段的 key 是随机 ID
    const fieldIdMap
      = INHOUSE_WEB_FORM_MAP[
        userStore.loyaltyTierCode === LOYALTY_TIER.TierPremium ? WEB_FORM_TYPE.Premium : WEB_FORM_TYPE.Normal
      ]?.fieldIdMap;
    if (fieldIdMap) {
      baseParams[fieldIdMap[WEB_FORM_AUTOFILL_FIELD.userType]] = agentStore.userTypeName;
      baseParams[fieldIdMap[WEB_FORM_AUTOFILL_FIELD.enquiryType]] = agentStore.inhouseEnquiryTypeName;
    }
  } else {
    baseParams.chatkey = String(agentStore.shopeeChatKey);
    // 对 SF 来说，agentStore.enquiryType 保存的是 queue name，从中解析出 userTypeName 和 enquiryTypeName
    const [userTypeName, enquiryTypeName] = separateEnquiryType(agentStore.enquiryType);
    baseParams.userType = userTypeName;
    baseParams.enquiryType = enquiryTypeName.replace(/\&/g, '-');
  }

  const params = new URLSearchParams(baseParams);
  const webFormUrl = generateOfflineFormUrl();

  return `${webFormUrl}${webFormUrl.includes('?') ? '&' : '?'}${params}`;
};


// 使用接口进行填充
export const getOfflineFormUrlWithParamsByApi = async () => {
  const baseParams = {} as any;
  // 移除sf的逻辑，保持webform的逻辑
  const webFormUuid = agentStore.webFormUuid || uuidv4();
  agentStore.updateWebFormUuid(webFormUuid);
  baseParams.uuid = webFormUuid;
  const { INHOUSE_WEB_FORM_MAP } = await import('@/utils/webform');
  // IH 自动填充字段的 key 是随机 ID
  const fieldIdMap
    = INHOUSE_WEB_FORM_MAP[
      userStore.loyaltyTierCode === LOYALTY_TIER.TierPremium ? WEB_FORM_TYPE.Premium : WEB_FORM_TYPE.Normal
    ]?.fieldIdMap;
  const autoFillInfoValueMap = getAutoFillInfoValueMap();

  const autoFillData: any = {};
  if (fieldIdMap) {
    Object.keys(fieldIdMap).forEach((key) => {
      if (key in WEB_FORM_AUTOFILL_FIELD) {
        // 支持自动填充
        const id = (fieldIdMap as any)[key];
        autoFillData[id] = (autoFillInfoValueMap as any)[key];
      }
    });
    // 请求后端接口，获取uuid
    const autoFillRes = await autoFillFrom({ autoFillData });
    if (!(autoFillRes instanceof Error)) {
      const { uuid } = autoFillRes;
      baseParams.fillToken = uuid;
    }
  }

  const params = new URLSearchParams(baseParams);
  const webFormUrl = generateOfflineFormUrl();

  return `${webFormUrl}${webFormUrl.includes('?') ? '&' : '?'}${params}`;
};
